import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Flame, Timer, Lock } from "lucide-react";

/* ============================================================
 *  BATCH CLOSING DATE  —  EDIT THIS LINE TO CHANGE THE COUNTDOWN
 *  ----------------------------------------------------------
 *  Format: "YYYY-MM-DDTHH:mm:ss"  (24-hour, local IST time)
 *  Example: "2026-06-30T23:59:59"  →  30 June 2026, 11:59:59 PM
 *
 *  - Update this string whenever a new batch is announced.
 *  - Save the file — the landing page picks it up instantly.
 *  - If the date is in the past, the section shows "Batch Closed".
 * ============================================================ */
const BATCH_CLOSING_DATE = "2026-06-06T11:00:00";

// Compute the remaining time between now and the target date.
// Returns { d, h, m, s, expired } where `expired` is true once the timer hits zero.
function getTimeLeft(targetMs) {
  const diff = targetMs - Date.now();
  if (diff <= 0) {
    return { d: 0, h: 0, m: 0, s: 0, expired: true };
  }
  return {
    d: Math.floor(diff / (1000 * 60 * 60 * 24)),
    h: Math.floor((diff / (1000 * 60 * 60)) % 24),
    m: Math.floor((diff / (1000 * 60)) % 60),
    s: Math.floor((diff / 1000) % 60),
    expired: false,
  };
}

export default function Urgency({ onCtaClick }) {
  // Parse the constant once. new Date("YYYY-MM-DDTHH:mm:ss") is treated as local time.
  const targetMs = new Date(BATCH_CLOSING_DATE).getTime();
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(targetMs));

  useEffect(() => {
    // Update immediately, then every second
    setTimeLeft(getTimeLeft(targetMs));
    const intervalId = setInterval(() => {
      const next = getTimeLeft(targetMs);
      setTimeLeft(next);
      // Stop the interval once the countdown reaches zero — no memory leaks
      if (next.expired) clearInterval(intervalId);
    }, 1000);

    // Cleanup on unmount or when the target changes
    return () => clearInterval(intervalId);
  }, [targetMs]);

  // Human-readable closing date for the small label above the timer
  const closingDate = new Date(targetMs);
  const dayName = closingDate.toLocaleDateString("en-IN", { weekday: "long" });
  const formattedDate = closingDate.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const closingTime = closingDate.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <section
      className="relative py-20 md:py-28 px-6 md:px-10"
      data-testid="urgency-section"
    >
      <div className="max-w-5xl mx-auto text-center bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 rounded-3xl p-10 md:p-14 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#38BDF8]/20 blur-[100px]" />

        <div className="relative">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest border ${
              timeLeft.expired
                ? "bg-slate-500/10 border-slate-500/30 text-slate-300"
                : "bg-red-500/10 border-red-500/30 text-red-400 animate-slow-pulse"
            }`}
          >
            {timeLeft.expired ? (
              <>
                <Lock className="w-3.5 h-3.5" /> Registrations Closed
              </>
            ) : (
              <>
                <Flame className="w-3.5 h-3.5" /> Filling Fast
              </>
            )}
          </div>

          {/* Heading */}
          <h2 className="font-display text-4xl md:text-5xl font-normal text-white mt-6 leading-tight">
            {timeLeft.expired ? "Batch Closed" : "Workshop starts in"}
          </h2>

          {/* Closing date label */}
          <div
            className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm md:text-base text-slate-300"
            data-testid="closing-date"
          >
            <span className="text-[#38BDF8] font-semibold uppercase tracking-widest text-xs md:text-sm">
              {dayName}
            </span>
            <span className="text-slate-600">·</span>
            <span className="font-medium">{formattedDate}</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-400">{closingTime} IST</span>
          </div>

          {/* Countdown OR Batch Closed message */}
          {timeLeft.expired ? (
            <div
              className="mt-10 max-w-2xl mx-auto"
              data-testid="batch-closed-message"
            >
              <div className="inline-block bg-[#0A1628] border border-slate-800 rounded-2xl px-8 py-6">
                <div className="font-display text-4xl md:text-5xl font-bold text-slate-500">
                  Batch Closed
                </div>
                <div className="text-sm text-slate-400 mt-3">
                  Registrations for this cohort are now closed.
                </div>
              </div>
              <p className="mt-6 text-slate-400">
                Drop your details below to be the first to know when the{" "}
                <span className="text-[#38BDF8] font-semibold">next batch</span>{" "}
                opens.
              </p>
            </div>
          ) : (
            <div
              className="mt-8 flex justify-center gap-3 md:gap-5"
              data-testid="countdown-timer"
            >
              {[
                { v: timeLeft.d, l: "Days" },
                { v: timeLeft.h, l: "Hours" },
                { v: timeLeft.m, l: "Minutes" },
                { v: timeLeft.s, l: "Seconds" },
              ].map((t, i) => (
                <div
                  key={i}
                  className="bg-[#0A1628] border border-slate-800 rounded-2xl px-5 py-4 md:px-7 md:py-5 min-w-[70px] md:min-w-[90px]"
                  data-testid={`countdown-${t.l.toLowerCase()}`}
                >
                  <div className="font-display text-3xl md:text-5xl font-normal text-[#38BDF8] tabular-nums">
                    {String(t.v).padStart(2, "0")}
                  </div>
                  <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 mt-1">
                    {t.l}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Helper text — only when batch is still open */}
          {!timeLeft.expired && (
            <p className="mt-8 text-slate-400 max-w-xl mx-auto">
              Only <span className="text-[#38BDF8] font-semibold">15 seats</span>{" "}
              left for this cohort. AI is no longer optional, it’s the future of every industry. Don't miss the chance to future-proof your career with Automation + AI
            </p>
          )}

          {/* CTA */}
          <Button
            onClick={onCtaClick}
            className="btn-yellow h-14 px-10 mt-8 rounded-full font-bold uppercase tracking-wider text-sm"
            data-testid="urgency-cta-button"
          >
            <Timer className="w-4 h-4 mr-2" />
            {timeLeft.expired
              ? "Join Waitlist For Next Batch"
              : "Book My Seat Now"}
          </Button>
        </div>
      </div>
    </section>
  );
}